/**
 * AnnotatorDashboard — cloned from BossDashboard.jsx with annotator data sources.
 * Every visual element is identical to the boss dashboard.
 * We swap metrics one-by-one from here.
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useData } from '../context/DataProvider';
import { COLORS as ENUM_COLORS } from '../constants/enums';
import { useToast } from '../components/Toast';

// Theme-aware COLORS shadow for admin-dark legibility.
const COLORS = { ...ENUM_COLORS, TEAL: 'var(--text-success)', RED: 'var(--text-error)', GOLD: 'var(--text-warning)', BLUE: 'var(--text-secondary)' };
import { computeJuuniScore, fetchJuuniScoreData, getScoreColor } from '../services/juuniScoreService';
import { fetchCurrentJobsWithTimeEntries, fetchMyApplications, fetchBrowseJobs } from '../adapters/jobAdapter';
import { logHours as logTimeEntry } from '../services/timeEntryService';
import { fetchProgressBatch } from '../services/onboardingService';
import { getUserById, currentUserId } from '../data/users';
import { supabase } from '../lib/supabase';
import { safeQuery } from '../utils/safeSupabase';
import LoadingState from '../components/states/LoadingState';
import ErrorBoundary from '../components/ErrorBoundary';
import WeeklyBarChart from '../components/WeeklyBarChart';
import { computeWeeklyCostTrend, computeWeeklyAnnotationsTrend } from '../services/analyticsService';
import { getTagColor } from '../constants/tags';
import { BATCH_STATES } from '../utils/batchProgress';

export default function AnnotatorDashboard() {
  const navigate = useNavigate();
  const toast = useToast();
  const { user: authUser } = useAuth();
  const { currentUser: dbUser, dataSource, orgId } = useData();
  const staticUser = getUserById(currentUserId);
  const currentUser = dbUser ? { ...staticUser, ...dbUser } : staticUser;
  const hasLiveData = dataSource === 'supabase';

  const [currentJobs, setCurrentJobs] = useState({ onboarding: [], active: [], completed: [] });
  const [, setBrowseJobs] = useState([]);
  const [juuniScoreResult, setJuuniScoreResult] = useState(null);
  const [todayEntries, setTodayEntries] = useState({});   // assignmentId → { hours, note }
  const [weekEntries, setWeekEntries] = useState({});     // assignmentId → [entries]
  const [hourInputs, setHourInputs] = useState({});       // assignmentId → input string
  const [logStatus, setLogStatus] = useState({});          // assignmentId → 'saving'|'saved'|'error'
  const [allBatches, setAllBatches] = useState([]);
  const [batchStats, setBatchStats] = useState({ total: 0, completed: 0, active: 0, activeCompleted: 0 });
  const [recentBatches, setRecentBatches] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hoveredMetric, setHoveredMetric] = useState(null);
  const [timeFilter, setTimeFilter] = useState('All Time');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  // ─── Data fetching ───
  useEffect(() => {
    if (dataSource !== 'supabase') { setLoading(false); return; }
    let cancelled = false;
    const uid = dbUser?._dbId || dbUser?.id || authUser?.id;
    Promise.all([
      fetchCurrentJobsWithTimeEntries(),
      fetchBrowseJobs(),
      // Fetch batch stats for Completion gauge
      uid ? safeQuery(
        supabase.from('annotation_batch')
          .select('id, status, total_items, items_completed, items_accepted, items_rejected, submitted_at, started_at, job_id, job:job_id(title)')
          .eq('individual_id', uid)
          .eq('is_overlap', false)
          .order('submitted_at', { ascending: false, nullsFirst: false }),
        'annotatorDashboard-batches'
      ) : Promise.resolve({ data: [] }),
      // Fetch notifications from DB
      uid ? safeQuery(
        supabase.from('notification')
          .select('*')
          .eq('user_id', uid)
          .order('created_at', { ascending: false })
          .limit(20),
        'annotatorDashboard-notifications'
      ) : Promise.resolve({ data: [] }),
      // Fetch applications for pending assessment notifications
      fetchMyApplications(),
    ]).then(async ([jobs, dbBrowseJobs, batchRes, notifRes, myApps]) => {
      if (cancelled) return;
      setCurrentJobs(jobs);
      setBrowseJobs(dbBrowseJobs || []);
      // Store time entry data from enriched fetch
      if (jobs.todayEntries) setTodayEntries(jobs.todayEntries);
      if (jobs.weekEntries) setWeekEntries(jobs.weekEntries);
      // Pre-fill hour inputs from today's entries
      const prefill = {};
      for (const [aId, entry] of Object.entries(jobs.todayEntries || {})) {
        prefill[aId] = String(entry.hours);
      }
      setHourInputs(prefill);
      const batches = batchRes?.data || [];
      // Annotator-visible "active" work under 4-state (migration 201):
      //   In Progress = still working (may be fresh or bounced-back)
      //   In Review   = annotator done, SPL reviewing (counts as in-flight
      //                 from the annotator's perspective — can't start
      //                 next batch while previous is under review)
      // Delivered / Accepted are terminal-from-annotator-perspective.
      const activeBatches = batches.filter(b => [BATCH_STATES.IN_PROGRESS, BATCH_STATES.IN_REVIEW].includes(b.status));
      setBatchStats({
        total: batches.reduce((s, b) => s + (b.total_items || 0), 0),
        completed: batches.reduce((s, b) => s + (b.items_completed || 0), 0),
        active: activeBatches.reduce((s, b) => s + (b.total_items || 0), 0),
        activeCompleted: activeBatches.reduce((s, b) => s + (b.items_completed || 0), 0),
        rejected: batches.reduce((s, b) => s + (b.items_rejected || 0), 0),
        // "pending" = fresh-assigned-but-not-yet-started. Under 4-state that's
        // In Progress with items_completed=0 and no rejection_note (distinct
        // from a bounced-back batch that's also In Progress but needs rework).
        pendingBatches: batches.filter(b => b.status === BATCH_STATES.IN_PROGRESS && (b.items_completed || 0) === 0 && !b.rejection_note).length,
        inProgressBatches: batches.filter(b => b.status === BATCH_STATES.IN_PROGRESS).length,
      });
      setAllBatches(batches);
      setRecentBatches(batches.slice(0, 5));

      // Build notifications list: DB notifications + onboarding action items
      const dbNotifs = (notifRes?.data || []).map(n => ({
        id: n.id,
        type: n.type,
        title: n.title || n.type,
        body: n.body,
        link: n.link,
        icon: n.icon || '🔔',
        read: !!n.read_at,
        createdAt: n.created_at,
        source: 'db',
      }));

      // Generate onboarding action items for Onboarding-status assignments
      const onboardingJobs = [...jobs.onboarding];
      let onboardingNotifs = [];
      if (onboardingJobs.length > 0) {
        const assignmentIds = onboardingJobs.map(a => a.id).filter(Boolean);
        if (assignmentIds.length > 0) {
          const progressMap = await fetchProgressBatch(assignmentIds);
          onboardingNotifs = onboardingJobs.map(a => {
            const progress = progressMap[a.id];
            const rows = progress?.rows || [];
            const incomplete = rows.filter(r => r.status !== 'Completed' && r.status !== 'Skipped');
            const total = rows.length;
            const done = total - incomplete.length;
            return {
              id: `onboarding-${a.id}`,
              type: 'OnboardingAction',
              title: `Complete onboarding: ${a.job?.title || 'New Project'}`,
              body: total > 0 ? `${done}/${total} steps done` : 'Onboarding steps required',
              link: a.job?.id ? `/job/${a.job.id}` : '/my-work',
              icon: '⚡',
              read: false,
              createdAt: a.assignedAt || new Date().toISOString(),
              source: 'onboarding',
              priority: true,
            };
          });
        }
      }

      // Add batch action items (assigned but not started yet).
      // Under 4-state: In Progress + zero items completed + no rejection_note
      // (so fresh work, not a bounced-back batch awaiting rework).
      const unstartedBatches = batches.filter(b => b.status === BATCH_STATES.IN_PROGRESS && (b.items_completed || 0) === 0 && !b.rejection_note);
      const batchNotifs = unstartedBatches.slice(0, 3).map(b => ({
        id: `batch-${b.id}`,
        type: 'BatchAction',
        title: b.job?.title || 'New batch',
        body: `${b.total_items} items waiting to start`,
        link: b.job_id ? `/job/${b.job_id}` : '/my-work',
        read: false,
        createdAt: b.started_at || new Date().toISOString(),
        source: 'batch',
      }));

      // Add assessment notifications from applications (pending trial tasks)
      // Filter out jobs the user is already working on
      const workingJobIds = new Set([
        ...jobs.active.map(a => a.jobId),
        ...jobs.onboarding.map(a => a.jobId),
        ...jobs.completed.map(a => a.jobId),
      ].filter(Boolean));
      const pendingAssessments = (myApps || []).filter(app =>
        (app.stage === 'assessment' || app.stage === 'Assessment' || app.stage === 'Skills Evaluation') &&
        !workingJobIds.has(app.jobId)
      );
      const assessNotifs = pendingAssessments.slice(0, 3).map(app => ({
        id: `assess-${app.id}`,
        type: 'AssessmentAction',
        title: app.jobTitle || 'Assessment required',
        body: `Complete assessment to start working · ${app.domain || ''}`,
        link: app.jobId ? `/job/${app.jobId}` : '/assessments',
        read: false,
        createdAt: app.appliedDate || new Date().toISOString(),
        source: 'assessment',
        priority: true,
      }));

      // Combine: assessments first (blocking), then onboarding, batch actions, DB notifications
      setNotifications([...assessNotifs, ...onboardingNotifs, ...batchNotifs, ...dbNotifs].slice(0, 10));
      setLoading(false);
    }).catch(() => setLoading(false));
    return () => { cancelled = true; };
  }, [dataSource, dbUser?.id]);

  useEffect(() => {
    if (!hasLiveData || !orgId || !currentUser?.id) return;
    let cancelled = false;
    fetchJuuniScoreData(orgId).then(data => {
      if (cancelled) return;
      setJuuniScoreResult(computeJuuniScore(currentUser.id, data));
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [hasLiveData, orgId, currentUser?.id]);

  // ─── Time filter date range ───
  const getFilterRange = (filter) => {
    const now = new Date();
    switch (filter) {
      case 'This Week': {
        const start = new Date(now); start.setDate(now.getDate() - now.getDay()); start.setHours(0, 0, 0, 0);
        return { start, end: now };
      }
      case 'This Month': {
        const start = new Date(now.getFullYear(), now.getMonth(), 1);
        return { start, end: now };
      }
      case 'Last Month': {
        const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const end = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
        return { start, end };
      }
      case 'Custom': {
        if (!customStart || !customEnd) return null;
        const start = new Date(customStart + 'T00:00:00');
        const end = new Date(customEnd + 'T23:59:59');
        return start <= end ? { start, end } : null;
      }
      default: return null; // All Time — no filter
    }
  };
  const filterRange = getFilterRange(timeFilter);
  const inRange = (dateStr) => {
    if (!filterRange || !dateStr) return true;
    const d = new Date(dateStr);
    return d >= filterRange.start && d <= filterRange.end;
  };

  // ─── Derived metrics (filtered by timeframe) ───
  const allAssignments = [...currentJobs.active, ...currentJobs.onboarding, ...currentJobs.completed];
  const activeAssignments = [...currentJobs.active, ...currentJobs.onboarding];

  // Build raw-shape records for the analytics service (which expects Supabase
  // snake_case). The transformed camelCase comes out of jobAdapter, so we
  // re-shape here. Scopes automatically to this annotator via individualId.
  const myRawAssignments = allAssignments.map(a => ({
    job_id: a.jobId || a.job?.id,
    individual_id: authUser?.id,
    assigned_at: a.assignedAt || a.startedAt,
    completed_at: a.completedAt,
    hours_logged: a.hoursLogged || 0,
    // R11 BLOCKER-2 audit fix: `a.rate` is canonical (post-R11 jobAdapter fix).
    // Drop the legacy `confirmedRate || rate` which overrode R4 LOW-5 semantic.
    rate: a.rate || 0,
    job: { title: a.job?.title || a.jobTitle, domain: a.job?.domain || a.domain },
  }));
  const myRawBatches = (allBatches || []).map(b => ({
    job_id: b.job_id,
    individual_id: authUser?.id,
    // started_at is required — computeWeeklyAnnotationsTrend distributes items
    // across the batch's active window (started_at → submitted_at/now).
    started_at: b.started_at,
    submitted_at: b.submitted_at,
    qa_completed_at: b.qa_completed_at,
    items_completed: b.items_completed || 0,
    items_accepted: b.items_accepted,
    total_items: b.total_items || 0,
    is_overlap: b.is_overlap,
  }));

  // Project title + color maps (used by WeeklyBarChart tooltip + stacking)
  const myJobTitleMap = {};
  const myProjectColors = new Map();
  for (const a of allAssignments) {
    const jid = a.jobId || a.job?.id;
    if (!jid) continue;
    if (a.job?.title || a.jobTitle) myJobTitleMap[jid] = a.job?.title || a.jobTitle;
    const domain = a.job?.domain || a.domain;
    if (domain) myProjectColors.set(jid, getTagColor(domain));
  }

  const myEarningsTrend = computeWeeklyCostTrend(myRawAssignments, { individualId: authUser?.id, jobTitleMap: myJobTitleMap });
  const myAnnotationsTrend = computeWeeklyAnnotationsTrend(myRawBatches, { individualId: authUser?.id, jobTitleMap: myJobTitleMap });

  // Filter assignments by timeframe (based on started_at / assigned_at)
  const filteredAssignments = filterRange
    ? allAssignments.filter(a => inRange(a.startedAt || a.assignedAt))
    : allAssignments;

  // Filter batches by timeframe (based on submitted_at or started_at)
  const filteredBatches = filterRange
    ? allBatches.filter(b => inRange(b.submitted_at || b.started_at))
    : allBatches;

  const juuniScore = juuniScoreResult?.score ?? null;
  const juneDims = juuniScoreResult?.dimensions || null;
  const totalHours = filteredAssignments.reduce((s, a) => s + (a.hoursLogged || 0), 0);
  // R11 BLOCKER-2 audit fix: `a.rate` is canonical (post-R11 jobAdapter fix).
  const totalEarnings = filteredAssignments.reduce((s, a) => s + (a.totalEarned || (a.hoursLogged || 0) * (a.rate || 0)), 0);
  const avgRate = totalHours > 0 ? totalEarnings / totalHours : (currentUser?.current_rate || 0);
  const qualityScore = juneDims?.quality ?? currentUser?.quality_score ?? null;

  // ─── Gauge metrics (filtered by timeframe) ───
  const gaugeJuuniScore = juuniScore;
  const gaugeJuuniSubtitle = gaugeJuuniScore != null
    ? `${gaugeJuuniScore >= 80 ? 'Excellent' : gaugeJuuniScore >= 60 ? 'Good' : 'Needs work'} standing`
    : 'No data yet';

  const gaugeQuality = qualityScore;
  const gaugeQualitySubtitle = gaugeQuality != null
    ? `${Math.round(gaugeQuality)}% accuracy`
    : 'No reviews yet';

  // Completion gauge — computed from filtered batches (same "active" semantic
  // as above: In Progress or In Review, excluding Delivered/Accepted terminal).
  const filteredActiveBatches = filteredBatches.filter(b => [BATCH_STATES.IN_PROGRESS, BATCH_STATES.IN_REVIEW].includes(b.status));
  const fBatchTotal = filteredBatches.reduce((s, b) => s + (b.total_items || 0), 0);
  const fBatchCompleted = filteredBatches.reduce((s, b) => s + (b.items_completed || 0), 0);
  const fBatchActiveTotal = filteredActiveBatches.reduce((s, b) => s + (b.total_items || 0), 0);
  const fBatchActiveCompleted = filteredActiveBatches.reduce((s, b) => s + (b.items_completed || 0), 0);
  const completionPct = fBatchActiveTotal > 0
    ? (fBatchActiveCompleted / fBatchActiveTotal) * 100
    : (fBatchTotal > 0 ? (fBatchCompleted / fBatchTotal) * 100 : null);
  const gaugeCompletion = completionPct;
  // Chapter 3 close F8 (CRITIQUE-driven, 2026-05-04): subtitle scope
  // disambiguation. Pre-fix the no-active-batches branch read
  // "X of Y total" — annotators on /my-work saw e.g. "575/580 items"
  // for their current project but on the dashboard saw "845 of 850
  // total" with no scope cue. Different numbers (lifetime vs current
  // project) under the same gauge label "COMPLETION" was confusing.
  // Now: when there are no active batches, the subtitle explicitly
  // reads "of Y lifetime" so the scope shift is obvious.
  const gaugeCompletionSubtitle = fBatchActiveTotal > 0
    ? `${fBatchActiveCompleted} of ${fBatchActiveTotal} items`
    : (fBatchTotal > 0 ? `${fBatchCompleted} of ${fBatchTotal} lifetime` : 'No batches yet');

  // ─── Flags (always computed from all-time data) ───
  const flags = [];
  if (batchStats.rejected > 0) flags.push(`${batchStats.rejected} rejected`);
  if (batchStats.pendingBatches > 0) flags.push(`${batchStats.pendingBatches} unstarted`);
  if (qualityScore != null && qualityScore > 0 && qualityScore < 90) flags.push('quality below 90%');
  if (juuniScore != null && juuniScore < 60) flags.push('score needs work');
  if (activeAssignments.length === 0 && allAssignments.length > 0) flags.push('no active work');
  const flagCount = flags.length;

  // Filtered KPI annotations count
  const filteredAnnotations = filteredBatches.reduce((s, b) => s + (b.items_completed || 0), 0);

  // Suggested jobs: prefer live browse jobs, fallback to static matchedJobs

  // Greeting
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const firstName = currentUser?.name?.split(' ')[0] || authUser?.email?.split('@')[0] || '';

  if (loading) return <LoadingState kind="skeleton" rows={5} ariaLabel="Loading dashboard" style={{ padding: 40 }} />;

  return (
    <div>
      {/* ─── Header ─── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 600, color: 'var(--text-primary)', fontFamily: 'var(--font-sans)', margin: 0 }}>{greeting}, {firstName}</h1>
          <p style={{ fontSize: 12, color: 'var(--text-secondary)', fontFamily: 'var(--font-mono)', margin: '4px 0 0' }}>
            {currentUser?.primary_domain || 'Annotator'} &middot; {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
          </p>
        </div>
      </div>

      {/* ─── Time Filter Tabs (matches production boss layout) ─── */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 16, alignItems: 'center' }}>
        {['All Time', 'This Week', 'This Month', 'Last Month', 'Custom'].map(t => (
          <button key={t} onClick={() => setTimeFilter(t)} style={{
            padding: '6px 14px', fontSize: 12, fontFamily: 'var(--font-mono)', borderRadius: 4, cursor: 'pointer',
            border: t === timeFilter ? '1px solid rgba(var(--accent-teal-rgb),0.35)' : '1px solid var(--border)',
            background: t === timeFilter ? 'rgba(var(--accent-teal-rgb),0.12)' : 'transparent',
            color: t === timeFilter ? COLORS.TEAL : 'var(--text-muted)',
            fontWeight: t === timeFilter ? 600 : 400,
          }}>{t}</button>
        ))}
        {timeFilter === 'Custom' && (
          <>
            <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)}
              style={{ fontSize: 12, fontFamily: 'var(--font-mono)', padding: '5px 8px', border: '1px solid var(--border)', borderRadius: 4, color: 'var(--text-primary)', background: 'var(--bg-input)', outline: 'none' }} />
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>to</span>
            <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)}
              style={{ fontSize: 12, fontFamily: 'var(--font-mono)', padding: '5px 8px', border: '1px solid var(--border)', borderRadius: 4, color: 'var(--text-primary)', background: 'var(--bg-input)', outline: 'none' }} />
          </>
        )}
      </div>

      {/* ─── Circular Gauges (annotator-specific: Juuni Score, Quality, Completion) ─── */}
      <div style={{
        display: 'grid', gridTemplateColumns: '1fr auto 1fr auto 1fr', gap: 16,
        paddingBottom: 24, marginBottom: 24, borderBottom: '1px solid var(--border)',
      }}>
        <div style={{ position: 'relative' }}
          onMouseEnter={() => setHoveredMetric('juuni')}
          onMouseLeave={() => setHoveredMetric(null)}
        >
          <GaugeRing value={gaugeJuuniScore} label="Juuni Score" subtitle={gaugeJuuniSubtitle} color={gaugeJuuniScore != null ? getScoreColor(gaugeJuuniScore) : undefined} />
          {hoveredMetric === 'juuni' && juneDims && (
            <div style={{
              position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)',
              marginTop: 8, padding: '12px 16px', background: 'var(--bg-popover)', border: '1px solid var(--border-hover)',
              borderRadius: 8, boxShadow: '0 4px 20px rgba(0,0,0,0.4)', zIndex: 20, minWidth: 220,
              animation: 'slideDown 0.15s ease-out',
            }}>
              <div style={{ fontSize: 12, fontFamily: 'var(--font-mono)', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>Score Breakdown</div>
              {/* Phase 23 deep-dive #21 (2026-05-03): tooltip rebalanced from
                  4-dim 35/30/20/15 (legacy pre-rebalance per juuniScoreService.ts:21
                  "Re-balanced from 4-dim 35/30/20/15 in commit b8dc2f4") to the
                  canonical 5-dim 30/25/20/15/10. Velocity row was missing entirely
                  even though juuniScoreService computes it (line 240) and exposes
                  it via juneDims.velocity. Pre-fix: annotator hovers gauge, sees
                  weights that don't add to 100% if read literally and a different
                  story than what the score actually reflects. */}
              {[
                { label: 'Consistency', value: juneDims.consistency, weight: '30%', color: COLORS.TEAL },
                { label: 'Reliability', value: juneDims.reliability, weight: '25%', color: COLORS.BLUE },
                { label: 'Quality',     value: juneDims.quality,     weight: '20%', color: COLORS.TEAL },
                { label: 'Velocity',    value: juneDims.velocity,    weight: '15%', color: COLORS.PURPLE },
                { label: 'Availability', value: juneDims.availability, weight: '10%', color: COLORS.GOLD },
              ].map(d => (
                <div key={d.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', borderBottom: '1px solid var(--border-light)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ width: 3, height: 12, borderRadius: 2, background: d.color }} />
                    <span style={{ fontSize: 12, color: 'var(--text-primary)' }}>{d.label}</span>
                    <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>({d.weight})</span>
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 600, fontFamily: 'var(--font-mono)', color: d.value != null ? d.color : 'var(--text-muted)' }}>
                    {d.value != null ? `${Math.round(d.value)}%` : '—'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div style={{ width: 1, background: 'var(--border)', alignSelf: 'stretch' }} />
        <div style={{ position: 'relative' }}
          onMouseEnter={() => setHoveredMetric('quality')}
          onMouseLeave={() => setHoveredMetric(null)}
        >
          <GaugeRing value={gaugeQuality} label="Quality" subtitle={gaugeQualitySubtitle} />
          {hoveredMetric === 'quality' && (
            <div style={{
              position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)',
              marginTop: 8, padding: '12px 16px', background: 'var(--bg-popover)', border: '1px solid var(--border-hover)',
              borderRadius: 8, boxShadow: '0 4px 20px rgba(0,0,0,0.4)', zIndex: 20, minWidth: 220,
              animation: 'slideDown 0.15s ease-out',
            }}>
              <div style={{ fontSize: 12, fontFamily: 'var(--font-mono)', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>Quality Details</div>
              <div style={{ fontSize: 12, color: 'var(--text-primary)', lineHeight: 1.5 }}>
                Average accuracy across Quality reviews of your annotation batches. Target: 90%+.
              </div>
              {gaugeQuality != null && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0 0', marginTop: 6, borderTop: '1px solid var(--border-light)' }}>
                  <span style={{ fontSize: 12, color: 'var(--text-primary)' }}>Current</span>
                  <span style={{ fontSize: 13, fontWeight: 600, fontFamily: 'var(--font-mono)', color: gaugeQuality >= 90 ? COLORS.TEAL : gaugeQuality >= 80 ? COLORS.GOLD : COLORS.RED }}>
                    {Math.round(gaugeQuality)}%
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
        <div style={{ width: 1, background: 'var(--border)', alignSelf: 'stretch' }} />
        <div style={{ position: 'relative' }}
          onMouseEnter={() => setHoveredMetric('completion')}
          onMouseLeave={() => setHoveredMetric(null)}
        >
          <GaugeRing value={gaugeCompletion} label="Completion" subtitle={gaugeCompletionSubtitle} />
          {hoveredMetric === 'completion' && (
            <div style={{
              position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)',
              marginTop: 8, padding: '12px 16px', background: 'var(--bg-popover)', border: '1px solid var(--border-hover)',
              borderRadius: 8, boxShadow: '0 4px 20px rgba(0,0,0,0.4)', zIndex: 20, minWidth: 220,
              animation: 'slideDown 0.15s ease-out',
            }}>
              <div style={{ fontSize: 12, fontFamily: 'var(--font-mono)', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>Batch Progress</div>
              {[
                { label: 'Active items', value: batchStats.active, color: COLORS.TEAL },
                { label: 'Completed', value: batchStats.activeCompleted, color: COLORS.TEAL },
                { label: 'Total assigned', value: batchStats.total, color: 'var(--text-primary)' },
                { label: 'Total done', value: batchStats.completed, color: 'var(--text-primary)' },
                { label: 'Items Rejected', value: batchStats.rejected, color: batchStats.rejected > 0 ? COLORS.RED : 'var(--text-muted)' },
              ].map(d => (
                <div key={d.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', borderBottom: '1px solid var(--border-light)' }}>
                  <span style={{ fontSize: 12, color: 'var(--text-primary)' }}>{d.label}</span>
                  <span style={{ fontSize: 13, fontWeight: 600, fontFamily: 'var(--font-mono)', color: d.color }}>{d.value}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ─── KPI Stat Row (matches production 7-column layout exactly) ─── */}
      <ErrorBoundary section label="KPI Cards">
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0, marginBottom: 32 }}>
        {[
          { value: allAssignments.length > 0 ? activeAssignments.length : '—', label: 'Active Jobs', sub: currentJobs.completed.length > 0 ? `${currentJobs.completed.length} completed` : undefined, hoverId: 'kpi-active', hoverDesc: `${activeAssignments.length} active (Active + Onboarding status)${currentJobs.completed.length > 0 ? `\n${currentJobs.completed.length} completed assignments` : ''}` },
          { value: totalHours > 0 ? `${totalHours.toFixed(0)}h` : '—', label: filterRange ? 'Hours' : 'Total Hours', sub: filterRange ? timeFilter.toLowerCase() : (activeAssignments[0]?.hoursLogged ? `${activeAssignments[0].hoursLogged.toFixed(0)}h current project` : undefined), hoverId: 'kpi-hours', hoverDesc: `Hours logged from approved time entries${filterRange ? ` (${timeFilter.toLowerCase()})` : ' across all assignments'}.\nHours are synced automatically when you log time.` },
          // R11 MED-3 audit fix: display uses canonical `rate` (post-R11
          // jobAdapter). Raw `confirmedRate` is admin-only and could be null.
          { value: avgRate > 0 ? `$${avgRate.toFixed(0)}/hr` : '—', label: 'Avg Rate', sub: activeAssignments[0]?.rate ? `$${activeAssignments[0].rate} current` : undefined, hoverId: 'kpi-rate', hoverDesc: `Weighted average: total earned ÷ total hours.\n${activeAssignments.length > 0 ? `Current project rate: $${activeAssignments[0]?.rate || '—'}/hr` : 'No active assignments'}` },
          { value: totalEarnings > 0 ? (totalEarnings >= 1000 ? `$${(totalEarnings / 1000).toFixed(1)}K` : `$${Math.round(totalEarnings)}`) : '—', label: filterRange ? 'Earned' : 'Total Earned', sub: filterRange ? timeFilter.toLowerCase() : (filteredAssignments.length > 0 ? `${filteredAssignments.length} assignments` : undefined), hoverId: 'kpi-earned', hoverDesc: `Earnings = hours × confirmed rate per assignment.${filterRange ? `\nFiltered to ${timeFilter.toLowerCase()}.` : `\nLifetime across ${filteredAssignments.length} assignment${filteredAssignments.length !== 1 ? 's' : ''}.`}` },
          { value: filteredAnnotations > 0 ? filteredAnnotations.toLocaleString() : '—', label: 'Annotations', sub: fBatchTotal > 0 ? `of ${fBatchTotal.toLocaleString()} assigned` : undefined, hoverId: 'kpi-annotations', hoverDesc: `Annotation items completed from batch data.${fBatchTotal > 0 ? `\n${Math.round((filteredAnnotations / fBatchTotal) * 100)}% of ${fBatchTotal.toLocaleString()} total assigned items.` : ''}${filterRange ? `\nFiltered to ${timeFilter.toLowerCase()}.` : ''}` },
          { value: qualityScore != null ? `${Math.round(qualityScore)}%` : '—', label: 'Avg Quality', sub: qualityScore != null ? (qualityScore >= 90 ? 'above target' : 'needs improvement') : 'no data', hoverId: 'kpi-quality', hoverDesc: `Average accuracy from Quality reviews.\nTarget: 90%+. ${qualityScore != null ? (qualityScore >= 95 ? 'Excellent — top tier quality.' : qualityScore >= 90 ? 'Good — meeting expectations.' : 'Below target — review feedback on rejected items.') : 'No Quality reviews yet.'}` },
          { value: flagCount, label: 'Flags', sub: flagCount > 0 ? flags[0] : 'all clear', color: COLORS.RED, hoverId: 'flags', hoverDesc: flagCount > 0 ? null : 'No active flags. Flags appear when quality drops below threshold or deadlines are at risk.' },
        ].map((kpi, i, arr) => (
          <div key={i}
            onMouseEnter={() => setHoveredMetric(kpi.hoverId)}
            onMouseLeave={() => setHoveredMetric(null)}
            style={{ cursor: 'default', textAlign: 'center', padding: '12px 8px', borderRadius: 6, borderRight: i < arr.length - 1 ? '1px solid var(--border)' : 'none', position: 'relative' }}>
            <div style={{ fontSize: 32, fontWeight: 600, fontFamily: 'var(--font-mono)', color: kpi.color || 'var(--text-primary)', lineHeight: 1 }}>{kpi.value}</div>
            <div style={{ fontSize: 10, fontWeight: 500, color: 'var(--text-primary)', textTransform: 'uppercase', letterSpacing: '0.4px', marginTop: 6 }}>{kpi.label}</div>
            {kpi.sub && <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>{kpi.sub}</div>}
            {kpi.hoverId === 'flags' && hoveredMetric === 'flags' && flags.length > 0 && (
              <div style={{
                position: 'absolute', top: '100%', right: 0, marginTop: 8,
                padding: '10px 14px', background: 'var(--bg-card)', border: '1px solid var(--border)',
                borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', zIndex: 20, minWidth: 180,
                textAlign: 'left',
              }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', textTransform: 'uppercase', letterSpacing: '0.48px', marginBottom: 6 }}>Active Flags</div>
                {flags.map((f, fi) => (
                  <div key={fi} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 0', fontSize: 12, color: 'var(--text-primary)' }}>
                    <span style={{ width: 4, height: 4, borderRadius: '50%', background: COLORS.RED, flexShrink: 0 }} />
                    {f}
                  </div>
                ))}
              </div>
            )}
            {kpi.hoverDesc && hoveredMetric === kpi.hoverId && (
              <div style={{
                position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)',
                marginTop: 8, padding: '8px 12px', background: 'var(--bg-popover)', border: '1px solid var(--border-hover)',
                borderRadius: 6, boxShadow: '0 4px 16px rgba(0,0,0,0.35)', zIndex: 20, minWidth: 180, maxWidth: 240,
                textAlign: 'left', fontSize: 12, color: 'var(--text-primary)', lineHeight: 1.5, whiteSpace: 'pre-line',
              }}>
                {kpi.hoverDesc}
              </div>
            )}
          </div>
        ))}
      </div>
      </ErrorBoundary>

      {/* Juuni Score Breakdown removed — shown on hover instead */}

      {/* ─── Single row: Assignments | Batches | Notifications | Profile ─── */}
      <ErrorBoundary section label="Assignments & Status">
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: 16, marginBottom: 20 }}>

        {/* Col 1: Assignments (wide — this is the main column with time logging) */}
        <div style={{ background: 'var(--bg-stat)', border: '1px solid var(--border)', borderRadius: 10, padding: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <span style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-primary)' }}>
              My Assignments
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 11, fontFamily: 'var(--font-mono)', fontWeight: 600, color: 'var(--text-secondary)', background: 'var(--bg-card)', padding: '3px 8px', borderRadius: 4 }}>{allAssignments.length}</span>
              <span onClick={() => navigate('/my-work')} style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', cursor: 'pointer' }}>View all →</span>
            </div>
          </div>
          {/* Phase 20.8 T1-2.1 (2026-04-30): tabIndex=0 + role=region +
              aria-label so keyboard users can scroll this assignment list
              and screen readers announce it as a navigable region. WCAG
              2.2 SC 2.1.1 (Keyboard) — scrollable regions must be
              keyboard-focusable. */}
          <div tabIndex={0} role="region" aria-label="Active assignments" style={{ maxHeight: 400, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
            {allAssignments.length === 0 && (
              <div style={{ padding: '24px 16px', textAlign: 'center', fontSize: 12, color: 'var(--text-muted)' }}>No assignments yet — browse open jobs to get started</div>
            )}
            {allAssignments.map(a => {
              const job = a.job || {};
              const statusColor = a.status === 'Active' ? COLORS.TEAL : a.status === 'Completed' ? 'var(--text-muted)' : COLORS.GOLD;
              // R11 BLOCKER-2 audit fix: `a.rate` is canonical (post-R11 jobAdapter).
              const earnings = ((a.hoursLogged || 0) * (a.rate || 0));
              return (
                <div key={a.id} onClick={() => job.id && navigate(`/job/${job.id}`)}
                  style={{ padding: '12px 16px', background: 'var(--bg-card)', borderRadius: 8, cursor: 'pointer', boxShadow: 'rgba(0,0,0,0.04) 0px 1px 3px', transition: 'box-shadow 0.15s' }}
                  onMouseEnter={e => { e.currentTarget.style.boxShadow = 'rgba(93,202,165,0.15) 0px 2px 8px'; e.currentTarget.querySelector('[data-title]').style.color = 'var(--text-success)'; }}
                  onMouseLeave={e => { e.currentTarget.style.boxShadow = 'rgba(0,0,0,0.04) 0px 1px 3px'; e.currentTarget.querySelector('[data-title]').style.color = 'var(--text-primary)'; }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                    <div data-title style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)', transition: 'color 0.15s' }}>{job.title || 'Unknown'}</div>
                    <span style={{ fontSize: 12, fontWeight: 500, fontFamily: 'var(--font-mono)', color: statusColor }}>{a.status}</span>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>
                    {job.domain || '—'} · ${a.rate || '—'}/hr · {a.status === 'Active' ? 'in progress' : a.status?.toLowerCase()}
                    {a.status === 'Completed' && a.completedAt && <> · Completed {new Date(a.completedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</>}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', gap: 16, fontSize: 12, fontFamily: 'var(--font-mono)', color: 'var(--text-primary)' }}>
                      <span>{(a.hoursLogged || 0).toFixed(0)}h</span>
                      <span>${earnings > 1000 ? (earnings / 1000).toFixed(1) + 'K' : earnings.toFixed(0)} earned</span>
                    </div>
                    {a.status === 'Active' && (() => {
                      // Phase 16.5 / D1: external_tool_url per-job (no more hardcoded platform.pipelines.tech)
                      // Chapter 3 close F19 (CRITIQUE-driven, 2026-05-04): converted from <span> to
                      // <button> to fix a11y. Pre-fix the CTA was a <span onClick> with no role
                      // and no keyboard handler — keyboard-only users couldn't activate the most
                      // prominent annotator action. Mirrors the <button disabled={!url}> pattern
                      // already used in JobDetail.jsx and Jobs.jsx.
                      const url = a?.external_tool_url || a?.job?.external_tool_url;
                      return (
                        <button
                          type="button"
                          onClick={e => {
                            e.stopPropagation();
                            if (url) window.open(url, '_blank', 'noopener,noreferrer');
                          }}
                          disabled={!url}
                          title={url || 'Tool URL not set on this job'}
                          style={{
                            fontSize: 12, fontWeight: 600,
                            color: url ? 'var(--text-success)' : 'var(--text-muted)',
                            padding: '5px 12px',
                            border: '1px solid rgba(var(--accent-teal-rgb),0.4)',
                            borderRadius: 4,
                            cursor: url ? 'pointer' : 'not-allowed',
                            whiteSpace: 'nowrap',
                            background: 'transparent',
                            fontFamily: 'inherit',
                          }}>
                          Start Working →
                        </button>
                      );
                    })()}
                  </div>
                  {a.status === 'Active' && (() => {
                    const aId = a.id;
                    const todayEntry = todayEntries[aId];
                    const weekHrs = (weekEntries[aId] || []).reduce((s, e) => s + (parseFloat(e.hours) || 0), 0);
                    const status = logStatus[aId];
                    return (
                      <div onClick={e => e.stopPropagation()} style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--border-light)' }}>
                        <span style={{ fontSize: 12, color: 'var(--text-muted)', flexShrink: 0 }}>Today</span>
                        <input type="number" step="0.5" min="0" max="24" placeholder="0"
                          value={hourInputs[aId] || ''}
                          onChange={e => setHourInputs(prev => ({ ...prev, [aId]: e.target.value }))}
                          style={{ width: 64, padding: '4px 8px', fontSize: 12, fontFamily: 'var(--font-mono)', border: '1px solid var(--border)', borderRadius: 4, textAlign: 'center', outline: 'none', color: 'var(--text-primary)', background: 'var(--bg-input)' }}
                          onFocus={e => { e.target.style.borderColor = 'var(--accent-teal)'; }}
                          onBlur={e => { e.target.style.borderColor = 'var(--border)'; }}
                        />
                        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>h</span>
                        <button disabled={status === 'saving' || !(parseFloat(hourInputs[aId]) > 0)}
                          onClick={async () => {
                            const hours = parseFloat(hourInputs[aId]);
                            if (!hours || hours <= 0) return;
                            setLogStatus(prev => ({ ...prev, [aId]: 'saving' }));
                            const { error } = await logTimeEntry({ assignmentId: aId, jobId: a.jobId || job.id, orgId: currentUser?.org_id || null, hours });
                            if (error) {
                              setLogStatus(prev => ({ ...prev, [aId]: 'error' }));
                              // Chapter 3 close F15: toast on log failure (was silent fail) so
                              // annotators know retry is needed.
                              toast.error('Could not log hours: ' + (error.message || 'unknown error'));
                              setTimeout(() => setLogStatus(prev => ({ ...prev, [aId]: null })), 2000);
                            } else {
                              setLogStatus(prev => ({ ...prev, [aId]: 'saved' }));
                              // Chapter 3 close F15 (CRITIQUE-driven, 2026-05-04): toast on
                              // success. Pre-fix the inline log succeeded silently — annotators
                              // had to glean success from the button-color change to green +
                              // appearance of "Xh this week". Stripe / Linear / Notion all
                              // surface a toast for inline mutations. Now we match that bar.
                              toast.success(`Logged ${hours}h on ${job.title || 'this project'}`);
                              const todayStr = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })();
                              setTodayEntries(prev => ({ ...prev, [aId]: { hours, date: todayStr } }));
                              setWeekEntries(prev => {
                                const existing = (prev[aId] || []).filter(e => e.date !== todayStr);
                                return { ...prev, [aId]: [...existing, { date: todayStr, hours, status: 'approved', assignment_id: aId }] };
                              });
                              setTimeout(() => setLogStatus(prev => ({ ...prev, [aId]: null })), 2000);
                            }
                          }}
                          style={{
                            padding: '4px 12px', fontSize: 12, fontWeight: 500, borderRadius: 4, cursor: 'pointer', border: 'none',
                            background: status === 'saved' ? 'var(--text-success)' : status === 'error' ? 'var(--text-error)' : todayEntry ? 'var(--border)' : 'var(--text-success)',
                            // Button text is on a solid colored bg (green/red/grey) — use light text for contrast
                            color: status === 'saved' || (!todayEntry && status !== 'error') ? '#fff' : status === 'error' ? '#fff' : 'var(--text-primary)',
                            opacity: status === 'saving' || !(parseFloat(hourInputs[aId]) > 0) ? 0.5 : 1,
                          }}
                        >{status === 'saving' ? '...' : status === 'saved' ? '✓' : status === 'error' ? '!' : todayEntry ? 'Update' : 'Log'}</button>
                        <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 'auto' }}>{weekHrs > 0 ? `${weekHrs.toFixed(1)}h this week` : ''}</span>
                      </div>
                    );
                  })()}
                </div>
              );
            })}
          </div>
        </div>

        {/* Col 2: Batches */}
        <KanbanCol title="Batches" count={recentBatches.length} color={COLORS.TEAL}>
          {recentBatches.length === 0 ? (
            <div style={{ padding: '16px 8px', textAlign: 'center', fontSize: 12, color: 'var(--text-muted)' }}>No batches yet</div>
          ) : recentBatches.slice(0, 5).map((b, i) => {
            // 4-state status colors: Accepted (terminal positive) / Delivered
            // (sent to client) / In Review (SPL reviewing, blue) / default amber
            // for In Progress. No 'Rejected' state — bounces to In Progress.
            const statusColor =
              b.status === BATCH_STATES.ACCEPTED  ? COLORS.TEAL :
              b.status === BATCH_STATES.DELIVERED ? COLORS.TEAL :
              b.status === BATCH_STATES.IN_REVIEW ? COLORS.BLUE :
              COLORS.GOLD;
            return (
              <div key={b.id} onClick={() => b.job_id && navigate(`/job/${b.job_id}`)}
                style={{ padding: '10px 12px', marginBottom: 6, background: 'var(--bg-card)', borderRadius: 8, cursor: 'pointer' }}
                onMouseEnter={e => { e.currentTarget.querySelector('[data-title]').style.color = 'var(--text-success)'; }}
                onMouseLeave={e => { e.currentTarget.querySelector('[data-title]').style.color = 'var(--text-primary)'; }}
              >
                <div data-title style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)', transition: 'color 0.15s' }}>Batch #{recentBatches.length - i}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3 }}>{b.job?.title || `${b.items_completed}/${b.total_items} items`}</div>
                <div style={{ fontSize: 12, fontFamily: 'var(--font-mono)', color: statusColor, marginTop: 3, fontWeight: 500 }}>{b.status} · {b.items_completed}/{b.total_items}</div>
              </div>
            );
          })}
        </KanbanCol>

        {/* Col 3: Notifications */}
        <KanbanCol title="Notifications" count={notifications.filter(n => !n.read).length} color={COLORS.BLUE}>
          {notifications.length === 0 ? (
            <div style={{ padding: '16px 8px', textAlign: 'center', fontSize: 12, color: 'var(--text-muted)' }}>All caught up</div>
          ) : notifications.slice(0, 5).map(n => {
            const isPriority = n.priority || n.type === 'OnboardingAction';
            const isAction = n.source === 'onboarding' || n.source === 'batch';
            const statusColor = isPriority ? COLORS.GOLD : isAction ? COLORS.BLUE : 'var(--text-muted)';
            const statusLabel = isPriority ? 'Action' : isAction ? 'Ready' : 'New';
            return (
              <div key={n.id} onClick={() => n.link && navigate(n.link)}
                style={{ padding: '10px 12px', marginBottom: 6, background: 'var(--bg-card)', borderRadius: 8, cursor: n.link ? 'pointer' : 'default', opacity: n.read ? 0.6 : 1 }}
                onMouseEnter={e => { const t = e.currentTarget.querySelector('[data-title]'); if (t) t.style.color = 'var(--text-success)'; }}
                onMouseLeave={e => { const t = e.currentTarget.querySelector('[data-title]'); if (t) t.style.color = 'var(--text-primary)'; }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                  <div data-title style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, marginRight: 6, transition: 'color 0.15s' }}>{n.title}</div>
                  <span style={{ fontSize: 12, fontFamily: 'var(--font-mono)', color: statusColor, flexShrink: 0 }}>{statusLabel}</span>
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.body}</div>
                {n.link && <div style={{ fontSize: 12, fontFamily: 'var(--font-mono)', color: 'var(--text-primary)', marginTop: 4 }}>View →</div>}
              </div>
            );
          })}
        </KanbanCol>

        {/* Col 4: Profile */}
        <KanbanCol title="Profile" count={null} color={COLORS.BLUE}>
          {[
            { label: 'Work Auth', value: currentUser?.work_auth_status || 'Pending', color: currentUser?.work_auth_status === 'Verified' ? COLORS.TEAL : COLORS.GOLD, to: '/profile' },
            { label: 'Resume', value: currentUser?.resume_url ? 'Uploaded' : 'Not uploaded', color: currentUser?.resume_url ? COLORS.TEAL : 'var(--text-muted)', to: '/profile' },
            { label: 'Domain', value: currentUser?.primary_domain || 'Not set', color: 'var(--text-muted)', to: '/profile' },
            { label: 'Referral Code', value: currentUser?.referral_code || 'Not set', color: currentUser?.referral_code ? 'var(--text-primary)' : 'var(--text-muted)', to: '/settings' },
          ].map((item, i) => (
            <div key={i} onClick={() => navigate(item.to)}
              style={{ padding: '10px 12px', marginBottom: 6, background: 'var(--bg-card)', borderRadius: 8, cursor: 'pointer' }}
              onMouseEnter={e => { e.currentTarget.querySelector('[data-title]').style.color = 'var(--text-success)'; }}
              onMouseLeave={e => { e.currentTarget.querySelector('[data-title]').style.color = 'var(--text-primary)'; }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div data-title style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)', transition: 'color 0.15s' }}>{item.label}</div>
                <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>→</span>
              </div>
              <div style={{ fontSize: 12, color: item.color, marginTop: 2 }}>{item.value}</div>
            </div>
          ))}
        </KanbanCol>
      </div>
      </ErrorBoundary>

      {/* ─── Weekly Earnings Trend (per-annotator rename of boss's Labor Cost Trend) ─── */}
      {myEarningsTrend.some(w => w.value > 0) && (
        <div className="card" style={{ padding: 20, marginTop: 16 }}>
          <div style={{ fontSize: 12, fontFamily: 'var(--font-mono)', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 12 }}>
            Weekly Earnings Trend
          </div>
          <WeeklyBarChart
            data={myEarningsTrend}
            height={120}
            formatValue={(v) => v > 0 ? `$${v.toLocaleString()}` : ''}
            formatBreakdownValue={(b) => `$${b.value.toLocaleString()}`}
            projectColors={myProjectColors}
          />
        </div>
      )}

      {/* ─── Weekly Annotations Completed ─── */}
      {myAnnotationsTrend.some(w => w.value > 0) && (
        <div className="card" style={{ padding: 20, marginTop: 16 }}>
          <div style={{ fontSize: 12, fontFamily: 'var(--font-mono)', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 12 }}>
            Weekly Annotations Completed
          </div>
          <WeeklyBarChart
            data={myAnnotationsTrend}
            height={120}
            formatValue={(v) => v > 0 ? v.toLocaleString() : ''}
            formatBreakdownValue={(b) => `${b.value.toLocaleString()} items`}
            projectColors={myProjectColors}
          />
        </div>
      )}
    </div>
  );
}

// ─── Sub-components (identical to BossDashboard) ───

function KanbanCol({ title, count, children }) {
  return (
    <div style={{ background: 'var(--bg-stat)', border: '1px solid var(--border)', borderRadius: 10, padding: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <span style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-primary)' }}>{title}</span>
        {count != null && (
          <span style={{ fontSize: 11, fontFamily: 'var(--font-mono)', fontWeight: 600, color: 'var(--text-secondary)', background: 'var(--bg-card)', padding: '3px 8px', borderRadius: 4 }}>{count}</span>
        )}
      </div>
      {/* Phase 20.8 T1-2.1: tabIndex=0 + role=region for keyboard scroll. */}
      <div tabIndex={0} role="region" aria-label={title} style={{ maxHeight: 200, overflowY: 'auto' }}>
        {children}
      </div>
    </div>
  );
}

function GaugeRing({ value = null, label = '', subtitle = '', color }) {
  const size = 170;
  const strokeWidth = 6;
  const center = size / 2;
  const r = 79;
  const circumference = 2 * Math.PI * r;
  const isNA = value == null;
  const clamped = isNA ? 0 : Math.max(0, Math.min(100, value));
  const offset = circumference * (1 - clamped / 100);
  const autoColor = isNA ? 'var(--border)' : clamped >= 70 ? 'var(--text-primary)' : clamped >= 30 ? 'var(--text-warning)' : 'var(--text-error)';
  const strokeColor = color || autoColor;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
      <div style={{ position: 'relative', width: size, height: size }}>
        <svg width={size} height={size}>
          <circle cx={center} cy={center} r={r} stroke="var(--border)" strokeWidth={strokeWidth} fill="none" />
          {!isNA && <circle cx={center} cy={center} r={r} stroke={strokeColor} strokeWidth={strokeWidth} fill="none"
            strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round"
            style={{ transition: 'stroke-dashoffset 0.6s', transformOrigin: 'center', transform: 'rotate(-90deg)' }} />}
        </svg>
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ fontSize: 10, fontWeight: 500, color: 'var(--text-muted)', letterSpacing: '0.4px', textTransform: 'uppercase', marginBottom: 2 }}>{label}</span>
          <span style={{ fontSize: 28, fontWeight: 600, fontFamily: 'var(--font-mono)', color: isNA ? 'var(--border)' : strokeColor }}>{isNA ? 'N/A' : `${Math.round(clamped)}%`}</span>
        </div>
      </div>
      {subtitle && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{subtitle}</span>}
    </div>
  );
}

// KPI component removed — KPI row rendered inline
